import "./SubmissionPage.scss";
import { Box } from "../../../components/styled-system/Box";
import { Spinner } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import Table from "react-bootstrap/Table";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { fetchSubmissionStatistics } from "../../../api/old-vine-registry-api";
import { SubmissionStatistics } from "../../../api/models";

const COLORS = [
  "#184472", "#2e7d32", "#e65100", "#6a1b9a", "#c62828",
  "#00838f", "#ef6c00", "#4527a0", "#1b5e20", "#ad1457",
];

const renderLabel = (entry: any) =>
  `${entry.name} (${entry.value})`;

export const SubmissionsTable: React.FC = () => {
  const [stats, setStats] = useState<SubmissionStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubmissionStatistics()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = "Submissions | Old Vine Registry";
    return () => { document.title = "Old Vine Registry"; };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <Spinner animation="border" role="status" />
      </Box>
    );
  }

  const topSubmittersThisMonth = stats?.topSubmittersThisMonth || [];
  const topSubmittersThisYear = stats?.topSubmittersThisYear || [];
  const monthlyVolume = stats?.monthlyVolume || [];
  const latestSubmissions = stats?.latestSubmissions || [];

  return (
    <div className="admin-panel">
      {/* Pie charts row */}
      <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", marginBottom: "32px" }}>
        {/* Top 10 submitters this month */}
        <div style={{ flex: "1", minWidth: "340px" }}>
          <h3 className="title-small">Top 10 Submitters — This Month</h3>
          {topSubmittersThisMonth.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={topSubmittersThisMonth}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  startAngle={90}
                  endAngle={-270}
                  label={renderLabel}
                >
                  {topSubmittersThisMonth.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="description-text" style={{ marginTop: "16px" }}>No submissions this month.</p>
          )}
        </div>

        {/* Top 10 submitters this year */}
        <div style={{ flex: "1", minWidth: "340px" }}>
          <h3 className="title-small">Top 10 Submitters — This Year</h3>
          {topSubmittersThisYear.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={topSubmittersThisYear}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  startAngle={90}
                  endAngle={-270}
                  label={renderLabel}
                >
                  {topSubmittersThisYear.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="description-text" style={{ marginTop: "16px" }}>No submissions this year.</p>
          )}
        </div>
      </div>

      {/* Monthly volume bar chart */}
      {monthlyVolume.length > 0 && (
        <div style={{ marginBottom: "32px" }}>
          <h3 className="title-small">Submissions per Month — {new Date().getFullYear()}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyVolume}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" fontSize={12} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#184472" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Last 20 submissions table */}
      <h3 className="title-small">Last 20 Submissions</h3>
      <Table hover size="sm" className="vineyard-table input-text u-mt-large">
        <thead>
          <tr>
            <th>Date</th>
            <th>Submitter Email</th>
            <th>Submitter Name</th>
            <th>Vineyard</th>
          </tr>
        </thead>
        <tbody>
          {latestSubmissions.map(row => (
            <tr key={row.id}>
              <td>{new Date(row.createdDate).toLocaleDateString()}</td>
              <td>{row.submitterEmail}</td>
              <td>{row.submitterName}</td>
              <td>{row.vineyardName}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};
